import React, { ReactNode, useLayoutEffect } from "react";
import Box, { Props as BoxProps } from "react-raster";
import { debounce } from "src/util/util";
import styled, { css } from "styled-components";
import { Text } from "../components";
import { Icon, IconId } from "../components/icon";
import { Color, theme } from "../config/theme";


const IconsContainer = styled.div<{ hasText: boolean }>`
  position:       absolute;
  top:            1.1rem;
  right:          1.6rem;
  display:        flex;
  flex-direction: row;

  ${p => p.hasText && css`
    &::before {
      z-index:       2;
      content:       attr(data-text);
      position:      absolute;
      top:           calc(-10px - 100% - 5px);
      left:          50%;
      color:         ${theme.color.black};
      font-size:     1.3rem;
      background:    ${theme.color.white};
      padding:       0.5rem 1rem;
      border-radius: 0.4rem;
      opacity:       0;
      visibility:    hidden;
      transition:    opacity 0.2s ease-out 0s, visibility 0s ease-out 0.2s;
      white-space:   nowrap;
      transform:     translateX(-50%);
      box-shadow:    0px 4px 8px rgba(8, 35, 48, 0.2);
    }

    &::after {
      content:      "";
      position:     absolute;
      transform:    translateX(-50%);
      opacity:      0;
      visibility:   hidden;
      transition:   opacity 0.2s ease-out 0s, visibility 0s ease-out 0.2s;
      border-top:   calc(-8px * -1) solid ${theme.color.white};
      border-left:  8px solid transparent;
      border-right: 8px solid transparent;
      top:          calc(-8px - 100% + 20px);
      left:         50%;
    }

    &:hover,
    &:focus {
      &::after,
      &::before {
        opacity:    1;
        visibility: visible;
        transition: opacity 0.2s ease-out 0s, visibility 0s ease-out 0s;
      }
    }
  `}
`;

const ErrorMessage = styled.span`
  display:    block;
  color:      ${theme.color.error};
  font-size:  ${theme.font.size.small};
  margin-top: 0.5rem;
`;

const InputField = styled.input<{ error: boolean, focused?: boolean, icons?: ActionIcon[]; color?: Color | string, background: string }>`
  -webkit-appearance: none;
  -moz-appearance:    none;
  appearance:         none;
  display:            block;
  font-size:          1.6rem;
  line-height:        2rem;
  width:              100%;
  background:         ${(p) => p.background ? p.background : 'none'};
  color:              ${(p) => p.color in theme.color ? `${theme.color[p.color as Color]}` : p.color || `${theme.font.color.black}`};
  background-color:   ${(p) => p.background ? p.background : 'transparent'};
  outline:            none;
  padding:            1rem;
  padding-right:      ${(p) => p.icons?.length == 2 ? '6rem' : '4rem'};
  transition:         all 0.3s ease;
  border:             1px solid;
  border-radius:      0.4rem;
  border-color:       ${theme.color.input.border};

  ::-webkit-inner-spin-button{
    -webkit-appearance: none;
    margin:             0;
  };
  ::-webkit-outer-spin-button{
    -webkit-appearance: none;
    margin:             0;
  };    

  &:focus {
    border-color:     ${p => p.color == 'dark' ? theme.color.black : theme.color.input.border};
    transition-delay: 0.1s;
  };

  ${p => p.error === true && css`
    border-color: ${theme.color.error};
  `};
`;

export const InputLabel = styled.label<{ focused: boolean, className?: string, }>`
  position:         absolute;
  font-size:        ${theme.font.size.small};
  /* text-transform:   capitalize; */
  top:              -1.2rem;
  left:             1.3rem;
  padding:          0 0.3rem;
  color:            ${theme.font.color.placeholder};
  background-color: ${ p => p.focused ? theme.color.white : 'transparent'};
  pointer-events:   none;
  transform-origin: 0% 0%;
  transform:        translateY(2.2rem) scale(1);
  transition:       transform 0.1s ease, background-color 0.1s ease;
  z-index:          1;


  ${p =>
    p.focused &&
    css`
      top:       -0.8rem;
      transform: translateY(0) scale(0.85);
    `};
`

export const InputGroup = styled.div`
  position: relative;
`;

type ActionIcon = {
  iconId: IconId;
  onClick?: () => void;
  action?: () => void; 
}


interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  maxLength?:       number;
  background?:      string;
  blurOnEnter?:     boolean;
  color?:           Color | string;
  debounced?:       boolean;
  editOnFocus?:     boolean;
  errorMessage?:    string;
  helperText?:      string;
  iconComponent?:   ReactNode;
  iconId?:          IconId;
  icons?:           ActionIcon[];
  id?:              string;
  label?:           string;
  placeholder?:     string;
  required?:        boolean;
  secondaryIconId?: IconId;
  showError?:       boolean;
  onKeyDown?:       (e: React.KeyboardEvent) => void;
  onBlur?:          (e: React.FocusEvent) => void;
  onChange?:        (value?: string) => void;
  onClickIcon?:     () => void;
  onEnter?:         () => void;
  onFocus?:         (e: React.FocusEvent<HTMLDivElement>) => void;
  setRef?:          (inputElement: HTMLInputElement) => void;
}

export const Input: React.FC<InputProps> = ({
  id,
  value,
  errorMessage,
  helperText,
  iconComponent,
  iconId,
  secondaryIconId,
  background,
  label       = "",
  type        = "text",
  showError   = false,
  debounced   = false,
  editOnFocus = false,
  color       = "dark",
  icons       = [],
  required    = false,
  blurOnEnter = true,
  onKeyDown   = () => {},
  onChange    = (text) => {
    console.error(`Missing onChange prop: ${text}`);
  },
  onFocus     = () => {},
  onBlur      = () => {},
  setRef      = () => {},
  onClickIcon = () => {},
  onEnter     = () => {},
  ...props
}) => {

  const [focused, setFocused] = React.useState(false);
  const input = React.useRef(null);
  const debouncedChange = React.useCallback(debounce(onChange), []);

  const handleOnFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    setFocused(true);
    onFocus(e);
  };

  const handleOnBlur = (e: React.FocusEvent) => {
    setFocused(false);
    onBlur(e);
  };

  const handleKeyDown = (e:  React.KeyboardEvent<HTMLDivElement>) => {
    if(e.key === "Enter" && blurOnEnter) {
      setFocused(false);
      e.currentTarget.blur();
  }
}

  const handleOnChange = (val: string) => {
    if (onChange) debounced ? debouncedChange(val) : onChange(val);
  };

  const isFocused: boolean = Boolean(
    focused || String(value).length || type === "date"
  );
  const hasIcon: boolean = Boolean( iconId || secondaryIconId || iconComponent || icons?.length);


  useLayoutEffect(() => {
    if(isFocused) setFocused(true)
  }, [isFocused])

  return (
    <InputGroup>
      {!focused && editOnFocus && value ? (
        <div onClick={() => setFocused(true)}>
          <Text>{value}</Text>
        </div>
      ) : (
        <>
          <InputField
            autoComplete = "off"
            autoFocus    = {editOnFocus}
            onBlur       = {handleOnBlur}
            onChange     = {(e) => handleOnChange(e.target.value)}
            onFocus      = {handleOnFocus}
            onKeyDown    = {handleKeyDown}
            error        = {showError}
            icons        = {icons}
            type         = {type}
            color        = {color}
            background   = {background}
            value        = {(value||'').toString()}
            ref          = {(ref) => {
              setRef(ref);
              input.current = ref;
            }}
            required = {required}
            onKeyUp  = {(event: React.KeyboardEvent<HTMLInputElement>) => {
              if(event.keyCode === 13) onEnter && onEnter();
            }}
            {...props}
          />

          {label !== undefined && (
            <InputLabel focused={isFocused}>{`${label} ${
              required ? " *" : ""
            }`}</InputLabel>
          )}

          {hasIcon && <IconsContainer data-text={helperText} hasText={helperText !== undefined}>
            {(icons?.length) && icons.map( icon => (
              <Icon 
                iconId  = {icon.iconId}
                key     = {icon.iconId}
                size    = "default"
                onClick = {icon.action}
                color   = {color}
                />
              ))}
          </IconsContainer>}
        </>
      )}
      {showError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </InputGroup>
  );
};


export const InputForm = ({ children, ...props }: BoxProps) => {
  const style = {
    ...props,
    display:       'flex',
    flexDirection: props.flexDirection || "column",
    gridgap:       props.gap || "0.5rem 0.5rem",
    gridRowGap:    props.gap || "0.5rem",
    margin:        '0.1rem 0rem 0.5rem 0rem'
  }
  return (
    <Box {...style}>
      {React.Children.map(children, (c, index) => (
        <Box key={index} cols={1}>{c}</Box>
      ))}
    </Box>
  );
};
